import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header, Footer, SEOHead } from '../components';
import { useSEO } from '../hooks/useSEO';
import { pageConfigs } from '../utils/seoConfig';
import { 
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  ArrowRightIcon,
  MagnifyingGlassIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  useSEO(pageConfigs.blog);

  const handleReadArticle = () => {
    // In a real app, this would navigate to the specific blog post
    navigate('/blog/lead-generation-guide-2024');
  };

  const handleSubscribeNewsletter = () => {
    // In a real app, this would handle newsletter subscription
    alert('Newsletter subscription functionality would be implemented here');
  };

  const handleGetConsultation = () => {
    navigate('/consultation');
  };

  const featuredPost = {
    title: "The Ultimate Guide to Lead Generation in 2024",
    excerpt: "Discover the latest strategies and tactics that are driving successful lead generation campaigns in today's digital landscape.",
    author: "Sarah Johnson",
    date: "March 15, 2024",
    readTime: "8 min read",
    category: "Lead Generation",
    image: "/api/placeholder/800/400"
  };

  const blogPosts = [
    {
      title: "Mobile App Development Trends to Watch in 2024",
      excerpt: "From AI integration to cross-platform development, explore the trends shaping mobile app development this year.",
      author: "Michael Chen",
      date: "March 12, 2024",
      readTime: "6 min read",
      category: "Mobile Development",
      image: "/api/placeholder/400/250"
    },
    {
      title: "How to Optimize Your Website for Better Conversions",
      excerpt: "Learn proven techniques to improve your website's conversion rate and turn more visitors into customers.",
      author: "Emily Rodriguez",
      date: "March 10, 2024",
      readTime: "7 min read",
      category: "Web Development",
      image: "/api/placeholder/400/250"
    },
    {
      title: "Social Media Marketing: Best Practices for 2024",
      excerpt: "Stay ahead of the curve with the latest social media marketing strategies that drive engagement and growth.",
      author: "David Thompson",
      date: "March 8, 2024",
      readTime: "5 min read",
      category: "Social Media",
      image: "/api/placeholder/400/250"
    },
    {
      title: "The ROI of Digital Marketing: Measuring Success",
      excerpt: "Understanding how to measure and optimize your digital marketing ROI for maximum business impact.",
      author: "Sarah Johnson",
      date: "March 5, 2024",
      readTime: "9 min read",
      category: "Digital Marketing",
      image: "/api/placeholder/400/250"
    },
    {
      title: "Google Ads vs. Meta Ads: Which is Right for Your Business?",
      excerpt: "A comprehensive comparison of Google Ads and Meta Ads to help you choose the best platform for your goals.",
      author: "Emily Rodriguez",
      date: "March 3, 2024",
      readTime: "6 min read",
      category: "Paid Advertising",
      image: "/api/placeholder/400/250"
    },
    {
      title: "Building User-Friendly Mobile Apps: A Designer's Guide",
      excerpt: "Essential principles and best practices for creating mobile apps that users love and engage with.",
      author: "David Thompson",
      date: "March 1, 2024",
      readTime: "8 min read",
      category: "Mobile Development",
      image: "/api/placeholder/400/250"
    }
  ];

  const categories = [
    "All Posts",
    "Lead Generation",
    "Mobile Development",
    "Web Development",
    "Digital Marketing",
    "Social Media",
    "Paid Advertising"
  ];

  const recentPosts = [
    {
      title: "5 Common Lead Generation Mistakes to Avoid",
      date: "March 14, 2024"
    },
    {
      title: "The Future of E-commerce Development",
      date: "March 11, 2024"
    },
    {
      title: "Instagram Marketing Tips for Small Businesses",
      date: "March 9, 2024"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead config={pageConfigs.blog} />
      <Header />

      {/* Hero Section */}
      <section className="pt-40 section-padding bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
              <DocumentTextIcon className="w-5 h-5 text-blue-600" />
              <span className="text-blue-700 font-semibold text-sm">3 Arrows Media Blog</span>
            </div>
            
            <h1 className="text-3xl lg:text-4xl font-display font-bold text-slate-900 mb-6 leading-tight">
              Digital Marketing
              <span className="text-blue-600"> Insights</span>
            </h1>
            
            <p className="text-lg text-slate-700 mb-8 leading-relaxed">
              Stay ahead of the curve with expert insights, industry trends, and actionable 
              strategies to grow your business in the digital world.
            </p>
            
            <div className="relative max-w-md mx-auto">
              <input
                type="text"
                placeholder="Search articles..."
                className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <MagnifyingGlassIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-slate-50 rounded-2xl overflow-hidden shadow-lg">
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="bg-gradient-to-br from-blue-100 to-slate-100 p-12 flex items-center justify-center">
                <DocumentTextIcon className="w-32 h-32 text-blue-600" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-3 py-1 mb-4 w-fit">
                  <TagIcon className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700 font-semibold text-sm">{featuredPost.category}</span>
                </div>
                
                <h2 className="text-3xl font-display font-bold text-slate-900 mb-4">
                  {featuredPost.title}
                </h2>
                
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {featuredPost.excerpt}
                </p>
                
                <div className="flex items-center space-x-6 text-sm text-slate-500 mb-6">
                  <div className="flex items-center space-x-2">
                    <UserIcon className="w-4 h-4" />
                    <span>{featuredPost.author}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="w-4 h-4" />
                    <span>{featuredPost.date}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <ClockIcon className="w-4 h-4" />
                    <span>{featuredPost.readTime}</span>
                  </div>
                </div>
                
                <button 
                  onClick={handleReadArticle}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center space-x-2 w-fit"
                >
                  <span>Read Full Article</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Category Filter */}
              <div className="flex flex-wrap gap-2 mb-8">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                      index === 0
                        ? 'bg-slate-900 text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Blog Posts */}
              <div className="grid md:grid-cols-2 gap-8">
                {blogPosts.map((post, index) => (
                  <article key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
                    <div className="h-48 bg-gradient-to-br from-blue-100 to-slate-100 flex items-center justify-center">
                      <DocumentTextIcon className="w-16 h-16 text-blue-600" />
                    </div>
                    
                    <div className="p-6">
                      <div className="inline-flex items-center space-x-2 bg-blue-100 rounded-full px-3 py-1 mb-4">
                        <TagIcon className="w-3 h-3 text-blue-600" />
                        <span className="text-blue-700 font-semibold text-xs">{post.category}</span>
                      </div>
                      
                      <h3 className="text-xl font-display font-bold text-slate-900 mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      
                      <p className="text-slate-600 mb-4 text-sm leading-relaxed line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between text-xs text-slate-500 mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <UserIcon className="w-3 h-3" />
                            <span>{post.author}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <CalendarIcon className="w-3 h-3" />
                            <span>{post.date}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="w-3 h-3" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      
                      <button className="text-blue-600 hover:text-blue-700 font-semibold text-sm flex items-center space-x-1 transition-colors">
                        <span>Read More</span>
                        <ArrowRightIcon className="w-3 h-3" />
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              {/* Load More */}
              <div className="text-center mt-12">
                <button className="bg-slate-900 hover:bg-slate-800 text-white font-semibold py-3 px-8 rounded-lg transition-colors">
                  Load More Articles
                </button>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="space-y-8">
                {/* Newsletter Signup */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-xl font-display font-bold text-slate-900 mb-4">
                    Stay Updated
                  </h3>
                  <p className="text-slate-600 mb-4 text-sm">
                    Get the latest digital marketing insights delivered to your inbox.
                  </p>
                  <div className="space-y-3">
                    <input
                      type="email"
                      placeholder="Your email address"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                    <button 
                      onClick={handleSubscribeNewsletter}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                    >
                      Subscribe
                    </button>
                  </div>
                </div>

                {/* Recent Posts */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-xl font-display font-bold text-slate-900 mb-4">
                    Recent Posts
                  </h3>
                  <div className="space-y-4">
                    {recentPosts.map((post, index) => (
                      <div key={index} className="border-b border-slate-100 pb-4 last:border-b-0 last:pb-0">
                        <h4 className="font-semibold text-slate-900 text-sm mb-2 hover:text-blue-600 cursor-pointer transition-colors">
                          {post.title}
                        </h4>
                        <div className="flex items-center space-x-1 text-xs text-slate-500">
                          <CalendarIcon className="w-3 h-3" />
                          <span>{post.date}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact CTA */}
                <div className="bg-slate-900 rounded-2xl p-6 text-white">
                  <h3 className="text-xl font-display font-bold mb-4">
                    Need Help?
                  </h3>
                  <p className="text-slate-300 mb-4 text-sm">
                    Ready to implement these strategies? Let's discuss your project.
                  </p>
                  <button 
                    onClick={handleGetConsultation}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors text-sm"
                  >
                    Get Free Consultation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;